<template>
	<div
		class="process-conference-select"
		:class="[
			sizeClass,
			{ 'process-conference-select--open': open, 'process-conference-select--disabled': disabled },
		]"
	>
		<input
			v-if="name"
			type="hidden"
			class="process-conference-select__hidden"
			:name="name"
			:value="hiddenFieldValue"
			tabindex="-1"
			aria-hidden="true"
		/>
		<span class="process-conference-select__measure" aria-hidden="true">{{ measureText }}</span>
		<button
			type="button"
			class="process-conference-select__trigger form-control"
			:class="'process-conference-select__trigger--' + selectedOption.tone"
			:disabled="disabled"
			:aria-expanded="open ? 'true' : 'false'"
			:aria-haspopup="true"
			:aria-label="ariaLabel"
			@click.stop="toggle"
			@keydown="onTriggerKeydown"
		>
			<span class="process-conference-select__label">{{ selectedOption.label }}</span>
			<i class="fa fa-chevron-down process-conference-select__caret" aria-hidden="true" />
		</button>
		<ul
			v-show="open"
			class="process-conference-select__menu"
			role="listbox"
			:aria-label="ariaLabel"
			@click.stop
		>
			<li
				v-for="(opt, idx) in options"
				:key="idx"
				class="process-conference-select__item"
				:class="[
					'process-conference-select__item--' + opt.tone,
					{ 'process-conference-select__item--active': optionEquals(opt.value, normalizedValue) },
				]"
				role="option"
				:aria-selected="optionEquals(opt.value, normalizedValue) ? 'true' : 'false'"
				@click="selectOption(opt)"
			>
				<span class="process-conference-select__item-label">{{ opt.label }}</span>
				<i
					v-if="optionEquals(opt.value, normalizedValue)"
					class="fa fa-check process-conference-select__check"
					aria-hidden="true"
				/>
			</li>
		</ul>
	</div>
</template>

<script>
const DEFAULT_OPTIONS = [
	{ value: null, label: "Pendente", tone: "neutral" },
	{ value: true, label: "Conferido", tone: "success" },
	{ value: false, label: "Divergente", tone: "danger" },
];

/** Tri-state vindo do modelo / API (Boolean, string ou vazio). */
function normalizeTriState(v) {
	if (v === true || v === "true") {
		return true;
	}
	if (v === false || v === "false") {
		return false;
	}
	return null;
}

export default {
	name: "ProcessConferenceSelect",
	model: {
		prop: "value",
		event: "input",
	},
	props: {
		value: {
			default: null,
			validator(v) {
				return (
					v === null ||
					v === true ||
					v === false ||
					typeof v === "undefined" ||
					v === "true" ||
					v === "false" ||
					v === ""
				);
			},
		},
		/** Nome do campo no formulário (ex.: ceMercanteChecked) — alinha ao fluxo de save do processo. */
		name: {
			type: String,
			default: "",
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		ariaLabel: {
			type: String,
			default: "",
		},
		options: {
			type: Array,
			default() {
				return DEFAULT_OPTIONS;
			},
		},
		size: {
			type: String,
			default: "md",
			validator(v) {
				return ["sm", "md", "lg"].indexOf(v) !== -1;
			},
		},
	},
	data() {
		return {
			open: false,
			outsideTimer: null,
		};
	},
	computed: {
		sizeClass() {
			return `process-conference-select--size-${this.size}`;
		},
		normalizedValue() {
			return normalizeTriState(this.value);
		},
		/** Valor serializado no hidden input (string) para compatibilidade com o form. */
		hiddenFieldValue() {
			const v = this.normalizedValue;
			if (v === true) {
				return "true";
			}
			if (v === false) {
				return "false";
			}
			return "";
		},
		selectedOption() {
			const found = this.options.find((o) => this.optionEquals(o.value, this.normalizedValue));
			return found || this.options[0] || DEFAULT_OPTIONS[0];
		},
		measureText() {
			return this.options.reduce(
				(longest, o) => (String(o.label).length > String(longest).length ? o.label : longest),
				"",
			);
		},
	},
	beforeDestroy() {
		this.detachOutsideClick();
	},
	methods: {
		detachOutsideClick() {
			if (this.outsideTimer !== null) {
				window.clearTimeout(this.outsideTimer);
				this.outsideTimer = null;
			}
			document.removeEventListener("click", this.onDocumentClick, true);
		},
		optionEquals(a, b) {
			if (a === b) {
				return true;
			}
			const aNull = a === null || typeof a === "undefined";
			const bNull = b === null || typeof b === "undefined";
			return aNull && bNull;
		},
		toggle() {
			if (this.disabled) {
				return;
			}
			if (this.open) {
				this.detachOutsideClick();
				this.open = false;
				return;
			}
			this.open = true;
			this.$nextTick(() => {
				this.outsideTimer = window.setTimeout(() => {
					this.outsideTimer = null;
					document.addEventListener("click", this.onDocumentClick, true);
				}, 0);
			});
		},
		close() {
			this.detachOutsideClick();
			this.open = false;
		},
		onDocumentClick(e) {
			if (this.$el.contains(e.target)) {
				return;
			}
			this.close();
		},
		selectOption(opt) {
			if (this.disabled) {
				return;
			}
			const next = typeof opt.value === "undefined" ? null : opt.value;
			if (!this.optionEquals(next, this.normalizedValue)) {
				this.$emit("input", next);
				this.$emit("change", next);
			}
			this.close();
		},
		onTriggerKeydown(e) {
			if (this.disabled) {
				return;
			}
			if (e.key === "Escape") {
				e.preventDefault();
				this.close();
				return;
			}
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				this.toggle();
			}
		},
	},
};
</script>

<style scoped>
/*
 * Tamanhos: sm | md | lg (--pcs-*)
 * Largura = maior rótulo + padding + seta (grid: measure invisível define coluna)
 */
.process-conference-select {
	position: relative;
	display: inline-grid;
	vertical-align: middle;
	max-width: 100%;
	/* md (padrão) */
	--pcs-font-size: 0.8125rem;
	--pcs-line-height: 1.25;
	--pcs-border-width: 1px;
	--pcs-measure-py: 0.15rem;
	--pcs-measure-pl: 0.45rem;
	--pcs-measure-pr: 1.45rem;
	--pcs-trigger-py: 0.15rem;
	--pcs-trigger-pl: 0.4rem;
	--pcs-trigger-pr: 0.35rem;
	--pcs-gap: 0.25rem;
	--pcs-caret: 0.55rem;
	--pcs-menu-py: 0.25rem;
	--pcs-item-py: 0.3rem;
	--pcs-item-px: 0.55rem;
	--pcs-item-gap: 0.35rem;
	--pcs-check: 0.75rem;
	--pcs-focus-ring: 0.15rem;
}

.process-conference-select--size-sm {
	--pcs-font-size: 0.75rem;
	--pcs-line-height: 1.2;
	--pcs-border-width: 1px;
	--pcs-measure-py: 0.08rem;
	--pcs-measure-pl: 0.35rem;
	--pcs-measure-pr: 1.2rem;
	--pcs-trigger-py: 0.1rem;
	--pcs-trigger-pl: 0.32rem;
	--pcs-trigger-pr: 0.28rem;
	--pcs-gap: 0.2rem;
	--pcs-caret: 0.5rem;
	--pcs-menu-py: 0.2rem;
	--pcs-item-py: 0.22rem;
	--pcs-item-px: 0.45rem;
	--pcs-item-gap: 0.28rem;
	--pcs-check: 0.65rem;
	--pcs-focus-ring: 0.12rem;
}

.process-conference-select--size-lg {
	--pcs-font-size: 1rem;
	--pcs-line-height: 1.5;
	--pcs-border-width: 2px;
	--pcs-measure-py: 0.35rem;
	--pcs-measure-pl: 0.65rem;
	--pcs-measure-pr: 1.85rem;
	--pcs-trigger-py: 0.375rem;
	--pcs-trigger-pl: 0.65rem;
	--pcs-trigger-pr: 0.5rem;
	--pcs-gap: 0.35rem;
	--pcs-caret: 0.65rem;
	--pcs-menu-py: 0.35rem;
	--pcs-item-py: 0.45rem;
	--pcs-item-px: 0.75rem;
	--pcs-item-gap: 0.5rem;
	--pcs-check: 0.85rem;
	--pcs-focus-ring: 0.2rem;
}

.process-conference-select__measure,
.process-conference-select__trigger {
	grid-column: 1;
	grid-row: 1;
	align-self: center;
}

.process-conference-select__measure {
	visibility: hidden;
	white-space: nowrap;
	font-weight: 700;
	letter-spacing: 0.02em;
	font-size: var(--pcs-font-size);
	line-height: var(--pcs-line-height);
	box-sizing: border-box;
	border: var(--pcs-border-width) solid transparent;
	padding: var(--pcs-measure-py) var(--pcs-measure-pr) var(--pcs-measure-py) var(--pcs-measure-pl);
}

.process-conference-select__trigger {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--pcs-gap);
	width: 100%;
	min-width: 0;
	min-height: 0;
	height: auto;
	text-align: left;
	cursor: pointer;
	font-weight: 700;
	letter-spacing: 0.02em;
	font-size: var(--pcs-font-size);
	line-height: var(--pcs-line-height);
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	padding: var(--pcs-trigger-py) var(--pcs-trigger-pr) var(--pcs-trigger-py) var(--pcs-trigger-pl);
}

.process-conference-select__trigger:disabled {
	cursor: not-allowed;
	opacity: 0.65;
}

.process-conference-select__label {
	flex: 1 1 auto;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.process-conference-select__caret {
	flex-shrink: 0;
	font-size: var(--pcs-caret);
	opacity: 0.75;
	transition: transform 0.2s ease;
}

.process-conference-select--open .process-conference-select__caret {
	transform: rotate(180deg);
}

.process-conference-select__trigger--neutral {
	border-width: var(--pcs-border-width);
	border-color: #6c757d;
	background-color: #e9ecef;
	color: #343a40;
}

.process-conference-select__trigger--neutral:focus {
	border-color: #5a6268;
	box-shadow: 0 0 0 var(--pcs-focus-ring) rgba(108, 117, 125, 0.2);
	outline: none;
}

.process-conference-select__trigger--success {
	border-width: var(--pcs-border-width);
	border-color: #28a745;
	background-color: #d4edda;
	color: #155724;
}

.process-conference-select__trigger--success:focus {
	border-color: #218838;
	box-shadow: 0 0 0 var(--pcs-focus-ring) rgba(40, 167, 69, 0.2);
	outline: none;
}

.process-conference-select__trigger--danger {
	border-width: var(--pcs-border-width);
	border-color: #dc3545;
	background-color: #f8d7da;
	color: #721c24;
}

.process-conference-select__trigger--danger:focus {
	border-color: #c82333;
	box-shadow: 0 0 0 var(--pcs-focus-ring) rgba(220, 53, 69, 0.2);
	outline: none;
}

.process-conference-select__menu {
	position: absolute;
	left: 0;
	right: 0;
	top: calc(100% + 2px);
	z-index: 1060;
	margin: 0;
	padding: var(--pcs-menu-py) 0;
	list-style: none;
	background: #fff;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 0.25rem;
	box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.12);
	max-height: 14rem;
	overflow-y: auto;
}

.process-conference-select__item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--pcs-item-gap);
	padding: var(--pcs-item-py) var(--pcs-item-px);
	cursor: pointer;
	font-size: var(--pcs-font-size);
	font-weight: 600;
	transition: background-color 0.12s ease;
}

.process-conference-select__item:hover,
.process-conference-select__item:focus {
	background-color: #f1f3f5;
}

.process-conference-select__item--active {
	background-color: #e7f1ff;
}

.process-conference-select__item--neutral.process-conference-select__item--active {
	background-color: #e9ecef;
}

.process-conference-select__item--success.process-conference-select__item--active {
	background-color: #d4edda;
}

.process-conference-select__item--danger.process-conference-select__item--active {
	background-color: #f8d7da;
}

.process-conference-select__item-label {
	flex: 1 1 auto;
	min-width: 0;
}

.process-conference-select__check {
	flex-shrink: 0;
	font-size: var(--pcs-check);
	opacity: 0.85;
}

.process-conference-select--disabled .process-conference-select__trigger {
	pointer-events: none;
}
</style>
